import { Tabs } from "@newsela/angelou";
import { useEffect, useState, useMemo } from "react";
import { STANDARDS_SET_MODE } from "@/search/NewSearchPageMVP/types/IStandardsSetMode";
import { useStandardsModalContext } from "@/search/NewSearchPageMVP/provider/StandardsModalProvider";
import { useStandardsSearchContext } from "@/search/NewSearchPageMVP/provider/StandardsSearchProvider";
import { EditableStandardsSet } from "./EditableStandardsSet";
import { allTab } from "./AllStandardsList";
import {
  tabListCss,
  tabPanelAdjustmentsCss,
  tabPanelsCss,
} from "./StandardsModalTabs.styles";
import { useStandardsModalActiveTab } from "./hooks";
import { CreateStandardsSets } from "./CreateStandardsSets";

export const StandardsModalTabs = () => {
  const {
    getters: {
      createdStandardsSets,
      isEditingSet,
      modalTabs,
      draftStandardsSetId,
    },
    handlers: {
      setStandardsSetMode,
      switchEditDataOnTabChange,
      setIsEditingSet,
      setModalTabs,
      handleCreateDraftStandardsSet,
      setAllowModalTabAutoChanges,
    },
  } = useStandardsModalContext();

  const { isLoadingStandardsResults, resetStandardsSearch } =
    useStandardsSearchContext();

  const tabsClass = "standards-modal-tabs";
  const activeTab = useStandardsModalActiveTab(tabsClass);
  const [prevActiveTab, setPrevActiveTab] = useState<string | undefined>();

  const dynamicStandardsSetsTabs = useMemo(
    () =>
      createdStandardsSets.map((standardsSet) => ({
        name: standardsSet.title,
        panelId: standardsSet.id.toString(),
        panelContent: <EditableStandardsSet standardsSet={standardsSet} />,
      })),

    [createdStandardsSets],
  );

  useEffect(() => {
    const activeTabTitle = activeTab?.innerText.trim();
    if (
      activeTabTitle &&
      (!activeTabTitle?.toLowerCase().includes("create") ||
        !activeTabTitle?.toLowerCase().includes("all"))
    ) {
      setIsEditingSet(false);
      switchEditDataOnTabChange(activeTab);
    }
    if (activeTabTitle?.toLowerCase().includes("create")) {
      setStandardsSetMode(STANDARDS_SET_MODE.CREATE);
      handleCreateDraftStandardsSet();
    } else if (!isEditingSet) {
      setStandardsSetMode(STANDARDS_SET_MODE.VIEW);
    }
    if (activeTabTitle !== prevActiveTab) {
      if (prevActiveTab?.includes("all")) {
        setAllowModalTabAutoChanges(true);
      }
      setPrevActiveTab(activeTabTitle?.toLowerCase());
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      prevActiveTab &&
      (prevActiveTab.includes("all") || prevActiveTab.includes("create"))
    ) {
      const inputField = document.getElementById(
        "standards-modal-search-input",
      );
      if (inputField) {
        (inputField as HTMLInputElement).value = "";
      }
      resetStandardsSearch();
    }
  }, [prevActiveTab]);

  useEffect(() => {
    setModalTabs([
      allTab,
      ...dynamicStandardsSetsTabs,
      {
        name: "+ Create Standards Set",
        panelId: "createStandardsSet",
        panelContent: <CreateStandardsSets />,
      },
    ]);
  }, [
    createdStandardsSets.length,
    allTab,
    dynamicStandardsSetsTabs,
    draftStandardsSetId,
  ]);

  if (!modalTabs || modalTabs[0]?.panelId !== "all") {
    return null;
  }

  return (
    <Tabs
      __classNameFor={{
        tabList: tabsClass,
      }}
      horizontalSwitch="sm"
      initialPanelId={modalTabs[0]?.panelId}
      renderTabsAsButtons
      flavor="underlined"
      __cssFor={{
        root: tabPanelAdjustmentsCss,
        tabList: tabListCss(isLoadingStandardsResults),
        tabPanels: tabPanelsCss,
      }}
      panels={modalTabs}
      dataProps={{
        "data-testid": "standards-modal-tabs",
        "data-qa-selector": "standards-modal-tabs",
      }}
    />
  );
};
